import { ContextValue } from "@datadog/browser-rum";
import { useCallback } from "react";
import {
  addDatadogGlobalContext,
  setDatadogUser,
  trackDatadogAction,
  trackDatadogError,
  trackDatadogTiming,
} from "~/lib/datadog-rum";

/**
 * Hook for easy DataDog RUM integration
 * Provides methods to track user actions, errors, and performance metrics
 */
export function useDatadogRUM() {
  const setUser = useCallback(
    (user: {
      id?: string;
      name?: string;
      email?: string;
      [key: string]: string | number | boolean | undefined;
    }) => {
      setDatadogUser(user);
    },
    [],
  );

  const addGlobalContext = useCallback((key: string, value: ContextValue) => {
    addDatadogGlobalContext(key, value);
  }, []);

  const trackAction = useCallback(
    (name: string, context?: Record<string, string | number | boolean>) => {
      trackDatadogAction(name, context);
    },
    [],
  );

  const trackError = useCallback(
    (error: Error, context?: Record<string, string | number | boolean>) => {
      trackDatadogError(error, context);
    },
    [],
  );

  const trackTiming = useCallback((name: string, duration: number) => {
    trackDatadogTiming(name, duration);
  }, []);

  const trackPageView = useCallback(
    (
      pageName: string,
      additionalContext?: Record<string, string | number | boolean>,
    ) => {
      trackAction("page_view", {
        page: pageName,
        timestamp: Date.now(),
        ...additionalContext,
      });
    },
    [trackAction],
  );

  const trackFormSubmission = useCallback(
    (
      formName: string,
      success: boolean,
      additionalContext?: Record<string, string | number | boolean>,
    ) => {
      trackAction("form_submission", {
        form: formName,
        success,
        timestamp: Date.now(),
        ...additionalContext,
      });
    },
    [trackAction],
  );

  const trackFeatureUsage = useCallback(
    (
      featureName: string,
      additionalContext?: Record<string, string | number | boolean>,
    ) => {
      trackAction("feature_usage", {
        feature: featureName,
        timestamp: Date.now(),
        ...additionalContext,
      });
    },
    [trackAction],
  );

  return {
    setUser,
    addGlobalContext,
    trackAction,
    trackError,
    trackTiming,
    trackPageView,
    trackFormSubmission,
    trackFeatureUsage,
  };
}
